import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import VisionBoard from '../components/VisionBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DailyRitual = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('personal'); // personal, embody, reality_garden, community, actuality, brain
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);

  // Debug state changes
  useEffect(() => {
    console.log('🔍 AFFIRMATION INDEX CHANGED:', currentAffirmationIndex);
  }, [currentAffirmationIndex]);

  useEffect(() => {
    console.log('🔍 CURRENT STEP CHANGED:', currentStep);
  }, [currentStep]);
  const [communityAffirmations, setCommunityAffirmations] = useState([]);
  const [currentCommunityIndex, setCurrentCommunityIndex] = useState(0);
  const [integrationTimer, setIntegrationTimer] = useState(11);
  const [showTimer, setShowTimer] = useState(false);
  const [userFirstImage, setUserFirstImage] = useState(null);
  const [expandedCommunityImage, setExpandedCommunityImage] = useState(null);

  // Keyboard navigation - simplified
  useEffect(() => {
    const handleKeyPress = (event) => {
      console.log('⌨️ KEY PRESSED:', event.key);
      if (event.key === 'ArrowRight' || event.key === 'Enter') {
        event.preventDefault();
        console.log('⌨️ KEYBOARD TRIGGERING NEXT');
        if (currentStep === 'personal') {
          nextAffirmation();
        } else if (currentStep === 'embody') {
          nextAffirmation();
        } else if (currentStep === 'reality_garden') {
          nextAffirmation();
        } else if (currentStep === 'community') {
          nextCommunityAffirmation();
        } else if (currentStep === 'actuality') {
          nextAffirmation();
        } else if (currentStep === 'brain') {
          completeRitual();
        }
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        console.log('⌨️ KEYBOARD TRIGGERING PREVIOUS');
        if (currentStep === 'personal') {
          previousAffirmation();
        } else if (currentStep === 'community') {
          previousCommunityAffirmation();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentStep, currentAffirmationIndex, currentCommunityIndex]);

  // REDESIGNED: Use auth context user data directly - no separate fetch needed
  const affirmations = user?.affirmations || [];
  const affirmationImages = user?.affirmation_images || [];

  // Fetch user's first vision board image for Community Wisdom display
  useEffect(() => {
    const fetchUserFirstImage = async () => {
      if (token && user) {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          const response = await axios.get(`${API}/user-first-vision-image`, { headers });
          
          if (response.data.has_image) {
            setUserFirstImage(response.data.image_url);
          }
        } catch (error) {
          console.error('Error fetching user first image:', error);
        }
      }
    };

    fetchUserFirstImage();
  }, [user, token]);

  // Fetch community affirmations when reaching community step
  useEffect(() => {
    if (currentStep === 'community') {
      setCurrentCommunityIndex(0); // RESET to start at first affirmation
      fetchCommunityAffirmations();
    }
  }, [currentStep]);

  const fetchCommunityAffirmations = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/community-affirmations`, { headers });
      
      // Get the affirmations from the correct API endpoint
      const affirmations = response.data.affirmations || [];
      
      // Format them for the Daily Ritual display
      const formattedAffirmations = affirmations.map(affirmation => ({
        vision: affirmation.text,
        author: affirmation.secret_mystery_name || 'Anonymous Guardian',
        image: affirmation.first_vision_image
      }));
      
      setCommunityAffirmations(formattedAffirmations);
      // Don't set step here - let the integration timer handle the transition
    } catch (error) {
      console.error('Error fetching community affirmations:', error);
      
      // Fallback to defaults if API fails
      const defaults = [
        { vision: "I am aligned with the cosmic flow of abundance", author: "🌟 Stellar Voyager", image: null },
        { vision: "I weave light into every moment", author: "✨ Luminous Sage", image: null },
        { vision: "I navigate life with grace and wisdom", author: "🧭 Mystic Pathfinder", image: null },
        { vision: "I seek and find truth in all experiences", author: "🔮 Sacred Oracle", image: null },
        { vision: "I dance with the rhythm of life", author: "💃 Rhythm Guardian", image: null },
        { vision: "I am connected to the infinite source of love", author: "🌸 Lotus Dreamer", image: null },
        { vision: "I embrace transformation as sacred evolution", author: "🦋 Soul Whisperer", image: null },
        { vision: "I flow with the currents of divine guidance", author: "🌊 Wave Rider", image: null },
        { vision: "I walk in harmony with lunar wisdom", author: "🌙 Moon Walker", image: null },
        { vision: "I tend the sacred flame within", author: "🔥 Fire Keeper", image: null }
      ];
      
      setCommunityAffirmations(defaults);
      // Don't set step here - let the integration timer handle the transition
    }
  };

  const nextAffirmation = () => {
    console.log('🔥 ABSOLUTELY BUTTON CLICKED!');
    console.log('📊 Current state:', {
      currentStep,
      currentAffirmationIndex,
      affirmationsLength: affirmations.length,
      timestamp: new Date().toISOString()
    });
    
    if (currentStep === 'personal') {
      console.log('✅ In personal step, checking index...');
      if (currentAffirmationIndex < affirmations.length - 1) {
        const newIndex = currentAffirmationIndex + 1;
        console.log(`🎯 SETTING NEW INDEX: ${currentAffirmationIndex} → ${newIndex}`);
        setCurrentAffirmationIndex(newIndex);
        console.log('✅ setCurrentAffirmationIndex called with:', newIndex);
      } else {
        console.log('🎯 MOVING TO EMBODY STEP');
        setCurrentStep('embody');
      }
    } else if (currentStep === 'embody') {
      console.log('🎯 MOVING TO REALITY GARDEN STEP');
      setCurrentStep('reality_garden');
    } else if (currentStep === 'reality_garden') {
      console.log('🎯 MOVING TO COMMUNITY STEP');
      setCurrentStep('community');
    } else if (currentStep === 'community') {
      console.log('🎯 PROGRESSING THROUGH COMMUNITY AFFIRMATIONS');
      nextCommunityAffirmation();
    } else if (currentStep === 'actuality') {
      console.log('🎯 MOVING TO BRAIN STEP');
      setCurrentStep('brain');
    } else if (currentStep === 'brain') {
      console.log('🎯 COMPLETING RITUAL');
      completeRitual();
    }
    
    console.log('🔥 nextAffirmation function completed');
  };

  const previousAffirmation = () => {
    if (currentAffirmationIndex > 0) {
      setCurrentAffirmationIndex(currentAffirmationIndex - 1);
    }
  };

  const nextCommunityAffirmation = () => {
    if (currentCommunityIndex < communityAffirmations.length - 1) {
      setCurrentCommunityIndex(currentCommunityIndex + 1);
    } else {
      setCurrentStep('actuality');
    }
  };

  const previousCommunityAffirmation = () => {
    if (currentCommunityIndex > 0) {
      setCurrentCommunityIndex(currentCommunityIndex - 1);
    }
  };

  const completeRitual = async () => {
    console.log('🎯 Completing ritual...');
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/complete-daily-ritual`, {}, { headers });
      console.log('✅ Neuro-Training Protocol completed successfully');
      navigate('/protocol-complete', {
        state: {
          currentStreak: res.data?.current_streak,
          longestStreak: res.data?.longest_streak
        }
      });
    } catch (error) {
      console.error('❌ Error completing ritual:', error);
      navigate('/protocol-complete'); // Still navigate even on error
    }
  };

  if (!user || affirmations.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-100 via-teal-50 to-cyan-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <h2 className="text-2xl font-semibold text-emerald-800 mb-4">
            {!user ? 'Please log in to begin your ritual' : 'Create your affirmations first'}
          </h2>
          <p className="text-emerald-600 mb-6">
            {!user 
              ? 'Your daily ritual awaits your presence'
              : 'Add personal affirmations in your profile to begin your daily ritual'
            }
          </p>
          <button 
            onClick={() => navigate(!user ? '/login' : '/profile')}
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700"
          >
            {!user ? 'Go to Login' : 'Add Affirmations'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-100 via-teal-50 to-cyan-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-32 h-32 bg-emerald-300 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-teal-300 rounded-full blur-xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-cyan-200 rounded-full blur-2xl"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        
        {/* Header */}
        <div className="text-center pt-8 pb-4">
          {currentStep === 'personal' && (
            <>
              <h1 className="text-5xl md:text-6xl font-bold text-emerald-800 mb-2">
                MHB Neuro-Training Protocol
              </h1>
              <h2 className="text-2xl md:text-3xl font-medium text-emerald-700 mb-2">
                Alpha State Reality Crafting
              </h2>
              <h3 className="text-lg text-emerald-600 mb-4">
                Best Practices for Personal Evolution - {new Date().getFullYear()}
              </h3>
              <p className="text-emerald-600 text-lg">
                {currentAffirmationIndex + 1} of {affirmations.length}
              </p>
            </>
          )}
          
          {currentStep === 'reality_garden' && (
            <div></div>
          )}
          
          {(currentStep === 'embody' || currentStep === 'brain' || currentStep === 'actuality') && (
            <div></div>
          )}
          
          {currentStep === 'community' && (
            <>
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">The world we thrive in</h2>
              <p className="text-emerald-600 text-sm">
                Affirmation {currentCommunityIndex + 1} of {communityAffirmations.length}
              </p>
            </>
          )}
        </div>

        {/* Progress Bar */}
        <div className="w-full max-w-md mx-auto mb-8 px-6">
          <div className="bg-white/30 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full transition-all duration-500"
              style={{
                width: currentStep === 'personal' 
                  ? `${((currentAffirmationIndex + 1) / affirmations.length) * 25}%`
                  : currentStep === 'integration' ? '50%'
                  : currentStep === 'community' ? '60%'
                  : currentStep === 'community' ? '75%'
                  : '100%'
              }}
            ></div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex items-start justify-center px-6 pt-4">
          
          {/* Personal Affirmations */}
          {currentStep === 'personal' && (
            <div 
              className="w-full text-center relative"
            >
              
              {/* Desktop Layout - Images on sides */}
              <div className="hidden md:flex items-center justify-center space-x-4">
                
                {/* Left Images */}
                <div className="w-64 space-y-4">
                  <VisionBoard 
                    affirmationIndex={currentAffirmationIndex}
                    userImages={affirmationImages}
                    side="left"
                    offsets={[0, 1]}
                    allowExpand={true}
                  />
                </div>

                {/* Central Affirmation - MUCH WIDER */}
                <div className="flex-1 max-w-none mx-8">
                  <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-12 border border-white/30 shadow-2xl">
                    <p className="text-2xl md:text-3xl font-bold text-emerald-800 leading-relaxed mb-8">
                      {affirmations[currentAffirmationIndex]}
                    </p>
                    
                    {/* Reflection Questions */}
                    <div className="mb-6 text-emerald-700">
                      <p className="text-lg font-medium mb-2">I love this! How did I navigate to this authentic self?</p>
                      <p className="text-lg font-medium">What step, no matter how small, can I take today to expedite this state? Whatever it is{currentAffirmationIndex === affirmations.length - 1 ? ' I Do' : ''}</p>
                    </div>
                    
                    {/* Navigation Buttons */}
                    <div className="flex justify-center space-x-4">
                      {currentAffirmationIndex > 0 && (
                        <button
                          onClick={previousAffirmation}
                          className="px-6 py-3 bg-white/20 text-emerald-700 rounded-xl hover:bg-white/30 transition-all"
                        >
                          ← Previous
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          console.log('🔥 BUTTON ELEMENT CLICKED!', e);
                          console.log('🔥 Event details:', {
                            type: e.type,
                            target: e.target.textContent,
                            timeStamp: e.timeStamp
                          });
                          nextAffirmation();
                        }}
                        onMouseDown={() => console.log('🔥 BUTTON MOUSE DOWN')}
                        onMouseUp={() => console.log('🔥 BUTTON MOUSE UP')}
                        className="px-8 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium transform active:scale-95"
                      >
                        {currentAffirmationIndex < affirmations.length - 1 ? 'I Do' : 'Integrate ✨'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Mobile Instructions */}
                  <p className="mt-4 text-emerald-600 text-sm md:hidden">
                    Tap anywhere or swipe left to continue
                  </p>
                </div>

                {/* Right Images */}
                <div className="w-64 space-y-4">
                  <VisionBoard 
                    affirmationIndex={currentAffirmationIndex}
                    userImages={affirmationImages}
                    side="right"
                    offsets={[2, 3]}
                    allowExpand={true}
                  />
                </div>
              </div>

              {/* Mobile Layout - Images below */}
              <div className="md:hidden">
                <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-6 border border-white/30 shadow-2xl mb-6">
                  <p className="text-xl font-bold text-emerald-800 leading-relaxed mb-6">
                    {affirmations[currentAffirmationIndex]}
                  </p>
                  
                  {/* Reflection Questions */}
                  <div className="mb-6 text-emerald-700">
                    <p className="text-base font-medium mb-2">I love this! How did I navigate to this authentic self?</p>
                    <p className="text-base font-medium">What step, no matter how small, can I take today to expedite this state? Whatever it is{currentAffirmationIndex === affirmations.length - 1 ? ' I Do' : ''}</p>
                  </div>
                  
                  {/* Navigation Buttons */}
                  <div className="flex justify-center space-x-4">
                    {currentAffirmationIndex > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          previousAffirmation();
                        }}
                        className="px-4 py-2 bg-white/20 text-emerald-700 rounded-xl hover:bg-white/30 transition-all"
                      >
                        ← Previous
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        console.log('🔥 MOBILE BUTTON CLICKED!', e);
                        nextAffirmation();
                      }}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium transform active:scale-95"
                    >
                      {currentAffirmationIndex < affirmations.length - 1 ? 'I Do' : 'Integrate ✨'}
                    </button>
                  </div>
                </div>
                
                {/* Vision Board below affirmation */}
                <VisionBoard 
                  affirmationIndex={currentAffirmationIndex}
                  userImages={affirmationImages}
                  side="grid"
                  offsets={[0, 1, 2, 3]}
                  className="max-w-4xl mx-auto"
                  allowExpand={true}
                />
                
                <p className="mt-4 text-emerald-600 text-sm">
                  Tap anywhere or swipe left to continue
                </p>
              </div>
            </div>
          )}

          {/* Embody Video Step (moved from completion page) */}
          {currentStep === 'embody' && (
            <div className="max-w-4xl w-full text-center">
              <h1 className="text-4xl md:text-5xl font-light text-black leading-relaxed mb-3">
                EMBODY
              </h1>
              
              <p className="text-xl text-black/90 mb-6">
                Expand your field to include all of existence. Reach out and connect everything required to ascend to this course corrected timeline and reality. Embody this BEING.
              </p>

              <div className="flex justify-center">
                <video 
                  autoPlay 
                  muted 
                  playsInline
                  onEnded={() => {
                    setTimeout(() => setCurrentStep('reality_garden'), 1000);
                  }}
                  className="rounded-2xl shadow-2xl max-w-lg w-full"
                >
                  <source src="https://res.cloudinary.com/dodou1xkz/video/upload/v1751388869/mhb_videos/mhb_loop_completion.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              <button
                onClick={() => setCurrentStep('reality_garden')}
                className="mt-6 px-8 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium"
              >
                Continue to Reality Garden
              </button>
            </div>
          )}

          {/* Reality Garden Video Step (NEW) */}
          {currentStep === 'reality_garden' && (
            <div className="max-w-4xl w-full text-center">
              <h1 className="text-4xl md:text-5xl font-light text-black leading-relaxed mb-3">
                Reality Garden
              </h1>
              
              <p className="text-xl text-black/90 mb-6">
                The world we thrive in, embrace it with positive energy.
              </p>

              <div className="flex justify-center">
                <video 
                  autoPlay 
                  muted 
                  playsInline
                  onEnded={() => {
                    setTimeout(() => setCurrentStep('community'), 1000);
                  }}
                  className="rounded-2xl shadow-2xl max-w-lg w-full"
                >
                  <source src="https://customer-assets.emergentagent.com/job_modern-human-3/artifacts/vy25sspa_reality-garden.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              <button
                onClick={() => setCurrentStep('community')}
                className="mt-6 px-8 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium"
              >
                Continue to Community Wisdom
              </button>
            </div>
          )}

          {/* Community Affirmations - EXACT REALITY GARDEN FORMAT */}
          {currentStep === 'community' && (
            <div className="max-w-md w-full mx-auto">
              {/* Single Community Affirmation - EXACT Reality Garden Card */}
              {communityAffirmations.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  
                  {/* Vision Board Image - First image from user's vision board */}
                  {communityAffirmations[currentCommunityIndex]?.image && (
                    <div 
                      className="h-64 bg-gray-100 overflow-hidden cursor-pointer"
                      onClick={() => setExpandedCommunityImage(communityAffirmations[currentCommunityIndex]?.image)}
                    >
                      <img 
                        src={communityAffirmations[currentCommunityIndex]?.image} 
                        alt="Vision Board" 
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  
                  {/* Affirmation Content */}
                  <div className="p-6">
                    {/* Affirmation Text */}
                    <blockquote className="text-lg font-medium text-gray-800 mb-4 leading-relaxed italic">
                      "{communityAffirmations[currentCommunityIndex]?.vision}"
                    </blockquote>
                    
                    {/* Mystery Name */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm">🌸</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-600">
                          {communityAffirmations[currentCommunityIndex]?.author || 'Anonymous Dreamer'}
                        </span>
                      </div>
                      <div className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                        Affirmation
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Navigation Buttons */}
              <div className="flex justify-center space-x-4 mt-8">
                {currentCommunityIndex > 0 && (
                  <button
                    onClick={previousCommunityAffirmation}
                    className="px-6 py-3 bg-white/20 text-emerald-700 rounded-xl hover:bg-white/30 transition-all backdrop-blur-lg"
                  >
                    ← Previous
                  </button>
                )}
                <button
                  onClick={nextCommunityAffirmation}
                  className="px-8 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium"
                >
                  {currentCommunityIndex < communityAffirmations.length - 1 ? 'ABSOLUTELY' : 'Continue to 11th Actuality'}
                </button>
              </div>
            </div>
          )}

          {/* 11th Actuality */}
          {currentStep === 'actuality' && (
            <div className="max-w-2xl w-full text-center">
              <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-8 border border-white/30 shadow-2xl">
                <h2 className="text-3xl font-bold text-emerald-800 mb-6">11th Actuality</h2>
                <h3 className="text-lg font-medium text-emerald-600 mb-4">What we know to be good for the world</h3>
                
                <p className="text-xl md:text-2xl font-bold text-emerald-800 leading-relaxed mb-8">
                  "Together we create a world where consciousness expands, hearts connect, and every soul finds their authentic path to joy and abundance."
                </p>
                
                <div className="mb-8">
                  <div className="mb-4 flex justify-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_modern-human-3/artifacts/prs5oqs7_use%20for%2011th.%20actuality.jpg"
                      alt="Heart-shaped Earth"
                      className="w-64 h-64 object-contain"
                    />
                  </div>
                  <p className="text-emerald-600 font-medium">
                    Your daily practice is complete.
                  </p>
                  <p className="text-emerald-800 font-bold text-lg mt-2">
                    Modern Human Being
                  </p>
                </div>
                
                <button
                  onClick={() => setCurrentStep('brain')}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-medium text-lg"
                >
                  Yes
                </button>
              </div>
            </div>
          )}

          {/* Brain Video Step (moved from integration position) */}
          {currentStep === 'brain' && (
            <div className="max-w-4xl w-full text-center">
              <h1 className="text-4xl md:text-5xl font-light text-black leading-relaxed mb-3">
                Integration
              </h1>
              
              <p className="text-xl text-black/90 mb-3">
                Take a moment to feel the warm wave of co-creation!
              </p>

              <div className="text-lg text-black/80 leading-relaxed mb-4">
                Allowing it all to settle into your consciousness…
              </div>
              
              <div className="flex justify-center">
                <video 
                  autoPlay 
                  muted 
                  playsInline
                  onEnded={() => {
                    setTimeout(() => completeRitual(), 1000);
                  }}
                  className="rounded-2xl shadow-2xl max-w-lg w-full"
                >
                  <source src="https://res.cloudinary.com/dodou1xkz/video/upload/v1751388781/mhb_videos/mhb_brain_integration.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              
              <button
                onClick={completeRitual}
                className="mt-6 px-8 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all font-medium"
              >
                Complete Protocol
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Expanded Community Image Modal */}
      {expandedCommunityImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" 
          onClick={() => setExpandedCommunityImage(null)}
        >
          <div className="max-w-3xl max-h-3xl">
            <img 
              src={expandedCommunityImage} 
              alt="Vision Board" 
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyRitual;